import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import {
  FlatImageEditorHandle,
  FlatImageEditorPendingUploadState,
  FlatImageEditorUploadAllPendingResult,
  FlatImageItem,
  FlatImageUploadPayload,
} from '~/components/Common/Images/types';

import { getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

export interface SingleImageUploadFieldHandle {
  getValue: () => string;
  uploadAllPending: () => Promise<FlatImageEditorUploadAllPendingResult>;
}

interface Props {
  value?: string;
  disabled?: boolean;
  pendingUploadAspectRatio?: number;
  uploadHelperText?: string;
  uploadLabel?: string;
  onChange: (nextValue: string) => void;
  onPendingUploadStateChange?: (state: FlatImageEditorPendingUploadState) => void;
}

const EMPTY_UPLOAD_RESULT: FlatImageEditorUploadAllPendingResult = {
  uploadedCount: 0,
  failedCount: 0,
  failedItems: [],
};

const SingleImageUploadField = forwardRef<SingleImageUploadFieldHandle, Props>(function SingleImageUploadField(
  {
    value,
    disabled = false,
    pendingUploadAspectRatio,
    uploadHelperText,
    uploadLabel = 'Upload logo',
    onChange,
    onPendingUploadStateChange,
  },
  ref,
) {
  const { enqueueSnackbar } = useSnackbar();
  const editorRef = useRef<FlatImageEditorHandle | null>(null);
  const currentValueRef = useRef(value ?? '');
  const [pendingState, setPendingState] = useState<FlatImageEditorPendingUploadState>({
    isUploadingPending: false,
    pendingUploadCount: 0,
  });

  useEffect(() => {
    currentValueRef.current = value ?? '';
  }, [value]);

  const items = useMemo<Array<FlatImageItem>>(
    () =>
      value
        ? [
            {
              id: value,
              publicImageId: value,
              order: 1,
              displayId: value,
              canReorder: false,
            },
          ]
        : [],
    [value],
  );

  const handleItemsChange = (nextItems: Array<FlatImageItem>) => {
    const nextItem =
      nextItems.find((item) => item.publicImageId && item.publicImageId !== currentValueRef.current) ??
      nextItems[nextItems.length - 1];
    const nextValue = nextItem?.publicImageId || '';

    currentValueRef.current = nextValue;
    onChange(nextValue);
  };

  const handleUploadFile = async ({ file, filename }: FlatImageUploadPayload): Promise<FlatImageItem> => {
    try {
      const response = await uploadImage(file);
      const uploadedImageId = getUploadedImageId(response);

      if (!uploadedImageId) {
        throw new Error('Image service did not return an image id');
      }

      currentValueRef.current = uploadedImageId;

      return {
        id: uploadedImageId,
        publicImageId: uploadedImageId,
        order: 1,
        displayId: uploadedImageId,
        filename,
        canReorder: false,
      };
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to upload image');
      enqueueSnackbar(message, { variant: 'error' });
      reportError(error);
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return currentValueRef.current;
    },
    async uploadAllPending() {
      return editorRef.current?.uploadAllPending() ?? EMPTY_UPLOAD_RESULT;
    },
  }));

  return (
    <FlatImageEditor
      ref={editorRef}
      allowMultipleUploadSelection={false}
      deleteActionLabel="Delete logo"
      descriptionLabel="Logo description"
      disabled={disabled}
      enablePendingCropUpload
      items={items}
      pendingUploadAspectRatio={pendingUploadAspectRatio}
      showDeleteAction
      showDescriptionField={false}
      showReorderAction={false}
      uploadDisabled={disabled || pendingState.pendingUploadCount > 0}
      uploadHelperText={uploadHelperText}
      uploadLabel={uploadLabel}
      onItemsChange={handleItemsChange}
      onPendingUploadStateChange={(state) => {
        setPendingState(state);
        onPendingUploadStateChange?.(state);
      }}
      onUploadFile={handleUploadFile}
    />
  );
});

export default SingleImageUploadField;
